// 戰場操作效果處理器
// Battlefield Manipulation Effect Handlers

import type { GameState, Card } from '../../../types';
import type { ImmediateAction, EffectHandler } from '../EffectTriggerSystem';
import { getLogger } from '../../../utils/logger';

const log = getLogger('BattlefieldHandlers');

/**
 * 移動效果處理器
 */
export class MoveHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('移動') || text.includes('向左') || text.includes('向右');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使其向左或向右移動一格」
    const moveMatch = effectText.match(/使(其|[^移]+)向(左|右)移動(\d*)格?/);
    if (moveMatch) {
      actions.push({
        type: 'move',
        sourceCardId: '',
        customHandler: 'moveUnit',
        value: moveMatch[2] === '左' ? -1 : 1,
        targetSelector: {
          zone: 'battlefield',
          side: moveMatch[1] === '其' ? 'enemy' : 'self',
          count: 1,
        },
      });
    }

    // 解析「移動到敵方場上任意位置」
    const moveToEnemyMatch = effectText.includes('移動到敵方場上任意位置');
    if (moveToEnemyMatch) {
      actions.push({
        type: 'move',
        sourceCardId: '',
        customHandler: 'moveToEnemySide',
      });
    }

    // 解析「無法移動」
    if (effectText.includes('無法移動')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'cannotMove',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'cannotMove') {
      log.debug(`Unit cannot move`);
      return state;
    }

    if (action.customHandler === 'moveUnit') {
      const direction = action.value as number;
      log.debug(`Move unit ${direction > 0 ? 'right' : 'left'}`);
      // TODO: 實作移動邏輯
    }

    if (action.customHandler === 'moveToEnemySide') {
      log.debug(`Move unit to enemy side`);
      // TODO: 實作移動到敵方場上邏輯
    }

    return state;
  }
}

/**
 * 綁架效果處理器
 * 將敵方單位移到我方場上
 */
export class KidnapHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被綁架');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方場上X單位被綁架」
    const kidnapMatch = effectText.match(/使敵方(場上)?(\d*)個?([^被]+)?被綁架/);
    if (kidnapMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'kidnap',
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
          count: kidnapMatch[2] ? parseInt(kidnapMatch[2], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'kidnap') return state;

    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;

    // 敵方 side
    const enemySideKey = isPlayer1 ? 'player2Side' : 'player1Side';
    const selfSideKey = isPlayer1 ? 'player1Side' : 'player2Side';

    let newState = state;
    let kidnapped = 0;

    // 尋找敵方單位並綁架
    const kidnappedUnits: Card[] = [];

    const findAndRemove = (lineKey: 'frontLine' | 'backLine'): void => {
      if (kidnapped >= count) return;

      const side = newState.battlefield[enemySideKey];
      const line = [...side[lineKey]];

      for (let i = 0; i < line.length && kidnapped < count; i++) {
        const unit = line[i].unit;
        if (unit) {
          kidnappedUnits.push(unit);
          line[i] = { ...line[i], unit: null };
          kidnapped++;
        }
      }

      newState = {
        ...newState,
        battlefield: {
          ...newState.battlefield,
          [enemySideKey]: {
            ...newState.battlefield[enemySideKey],
            [lineKey]: line,
          },
        },
      };
    };

    findAndRemove('frontLine');
    findAndRemove('backLine');

    // 將綁架的單位放到我方場上空位
    const placeKidnapped = (): void => {
      for (const unit of kidnappedUnits) {
        const selfSide = newState.battlefield[selfSideKey];
        let placed = false;

        // 嘗試前排
        const frontLine = [...selfSide.frontLine];
        for (let i = 0; i < frontLine.length && !placed; i++) {
          if (!frontLine[i].unit) {
            frontLine[i] = {
              ...frontLine[i],
              unit: {
                ...unit,
                ownerId: playerId, // 改變擁有者
                position: {
                  side: isPlayer1 ? 'player1' : 'player2',
                  row: 'front',
                  column: (i + 1) as 1 | 2 | 3 | 4 | 5,
                },
              },
            };
            placed = true;

            newState = {
              ...newState,
              battlefield: {
                ...newState.battlefield,
                [selfSideKey]: {
                  ...newState.battlefield[selfSideKey],
                  frontLine,
                },
              },
            };
          }
        }

        // 嘗試後排
        if (!placed) {
          const backLine = [...selfSide.backLine];
          for (let i = 0; i < backLine.length && !placed; i++) {
            if (!backLine[i].unit) {
              backLine[i] = {
                ...backLine[i],
                unit: {
                  ...unit,
                  ownerId: playerId,
                  position: {
                    side: isPlayer1 ? 'player1' : 'player2',
                    row: 'back',
                    column: (i + 1) as 1 | 2 | 3 | 4 | 5,
                  },
                },
              };
              placed = true;

              newState = {
                ...newState,
                battlefield: {
                  ...newState.battlefield,
                  [selfSideKey]: {
                    ...newState.battlefield[selfSideKey],
                    backLine,
                  },
                },
              };
            }
          }
        }
      }
    };

    placeKidnapped();

    log.debug(`Kidnapped ${String(kidnapped)} units`);
    return newState;
  }
}

/**
 * 召喚到特定位置效果處理器
 */
export class SummonToPositionHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return (text.includes('被召喚') || text.includes('被額外打出')) &&
           (text.includes('至敵方場上') || text.includes('至你場上任意位置'));
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「被召喚至敵方場上」
    if (effectText.includes('被召喚至敵方場上') || effectText.includes('額外打出此卡至敵方場上')) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        customHandler: 'summonToEnemy',
        targetSelector: {
          zone: 'battlefield',
          side: 'enemy',
        },
      });
    }

    // 解析「被召喚至你場上任意位置」
    if (effectText.includes('至你場上任意位置')) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        customHandler: 'summonToAnyPosition',
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    log.debug((action.customHandler ?? 'unknown'));
    // TODO: 需要與主召喚邏輯整合
    return state;
  }
}

/**
 * 轉正/橫放效果處理器
 */
export class OrientationHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被轉正') || text.includes('被橫放');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方場上X單位被橫放」
    const tapMatch = effectText.match(/使(敵方|你)(場上)?(\d*)個?([^被]+)?被橫放/);
    if (tapMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'tap',
        targetSelector: {
          zone: 'battlefield',
          side: tapMatch[1] === '敵方' ? 'enemy' : 'self',
          count: tapMatch[3] ? parseInt(tapMatch[3], 10) : 1,
        },
      });
    }

    // 解析「使X被轉正」
    const untapMatch = effectText.match(/使(敵方|你)?(場上)?(\d*)個?([^被]+)?被轉正/);
    if (untapMatch && !effectText.includes('由破壞改為被橫放')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'untap',
        targetSelector: {
          zone: 'battlefield',
          side: untapMatch[1] === '敵方' ? 'enemy' : 'self',
          count: untapMatch[3] ? parseInt(untapMatch[3], 10) : 1,
        },
      });
    }

    // 解析「由破壞改為被橫放」
    if (effectText.includes('由破壞改為被橫放')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'destroyToTap',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const selector = action.targetSelector;
    if (!selector && action.customHandler !== 'destroyToTap') return state;

    const isPlayer1 = state.activePlayerId === state.player1.id;

    if (action.customHandler === 'tap') {
      const isEnemy = selector?.side === 'enemy';
      const sideKey = isEnemy ? 
        (isPlayer1 ? 'player2Side' : 'player1Side') :
        (isPlayer1 ? 'player1Side' : 'player2Side');
      const count = selector?.count ?? 1;

      let newState = state;
      let tapped = 0;

      const tapLine = (lineKey: 'frontLine' | 'backLine'): void => {
        if (tapped >= count) return;

        const side = newState.battlefield[sideKey];
        const line = side[lineKey].map((slot) => {
          if (slot.unit && slot.unit.orientation !== 'tapped' && tapped < count) {
            tapped++;
            return {
              ...slot,
              unit: { ...slot.unit, orientation: 'tapped' as const },
            };
          }
          return slot;
        });

        newState = {
          ...newState,
          battlefield: {
            ...newState.battlefield,
            [sideKey]: {
              ...newState.battlefield[sideKey],
              [lineKey]: line,
            },
          },
        };
      };

      tapLine('frontLine');
      tapLine('backLine');

      log.debug(`Tapped ${String(tapped)} units`);
      return newState;
    }

    if (action.customHandler === 'untap') {
      const isEnemy = selector?.side === 'enemy';
      const sideKey = isEnemy ?
        (isPlayer1 ? 'player2Side' : 'player1Side') :
        (isPlayer1 ? 'player1Side' : 'player2Side');
      const count = selector?.count ?? 1;

      let newState = state;
      let untapped = 0;

      const untapLine = (lineKey: 'frontLine' | 'backLine'): void => {
        if (untapped >= count) return;

        const side = newState.battlefield[sideKey];
        const line = side[lineKey].map((slot) => {
          if (slot.unit && slot.unit.orientation === 'tapped' && untapped < count) {
            untapped++;
            return {
              ...slot,
              unit: { ...slot.unit, orientation: 'upright' as const },
            };
          }
          return slot;
        });

        newState = {
          ...newState,
          battlefield: {
            ...newState.battlefield,
            [sideKey]: {
              ...newState.battlefield[sideKey],
              [lineKey]: line,
            },
          },
        };
      };

      untapLine('frontLine');
      untapLine('backLine');

      log.debug(`Untapped ${String(untapped)} units`);
      return newState;
    }

    if (action.customHandler === 'destroyToTap') {
      log.debug(`Destroy replaced with tap`);
      // 這個效果需要在戰鬥處理時判斷
    }

    return state;
  }
}

/**
 * 除外效果處理器
 */
export class ExileHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('被除外');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使此卡被除外」
    if (effectText.includes('使此卡被除外')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'exileSelf',
      });
    }

    // 解析「使X被除外」
    const exileMatch = effectText.match(/使(敵方|你)?(場上)?(\d*)個?([^被]+)?被除外/);
    if (exileMatch && !effectText.includes('使此卡被除外')) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'exile',
        targetSelector: {
          zone: 'battlefield',
          side: exileMatch[1] === '敵方' ? 'enemy' : 'self',
          count: exileMatch[3] ? parseInt(exileMatch[3], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler === 'exileSelf') {
      log.debug(`Card will be exiled`);
      // TODO: 實作除外邏輯（需要在使用後處理）
    }

    if (action.customHandler === 'exile') {
      log.debug(`Exile target`);
      // TODO: 實作除外邏輯
    }

    return state;
  }
}

/**
 * 從墓地召喚效果處理器
 */
export class GraveyardSummonHandler implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('墓地') && text.includes('被召喚');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使你墓地X單位被召喚」
    const graveSummonMatch = effectText.match(/使你墓地(\d*)張?([^被]+)?被召喚/);
    if (graveSummonMatch) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        customHandler: 'summonFromGraveyard',
        targetSelector: {
          zone: 'graveyard',
          side: 'self',
          filter: graveSummonMatch[2] ? { name: graveSummonMatch[2].trim() } : undefined,
          count: graveSummonMatch[1] ? parseInt(graveSummonMatch[1], 10) : 1,
        },
      });
    }

    // 解析「從你墓地額外打出此卡」
    if (effectText.includes('從你墓地額外打出此卡')) {
      actions.push({
        type: 'summon',
        sourceCardId: '',
        customHandler: 'playFromGraveyard',
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    if (action.customHandler !== 'summonFromGraveyard') return state;

    const selector = action.targetSelector;
    if (!selector) return state;

    const count = selector.count ?? 1;
    const cardName = selector.filter?.name;
    const playerId = state.activePlayerId;
    const isPlayer1 = playerId === state.player1.id;
    const playerKey = isPlayer1 ? 'player1' : 'player2';
    const sideKey = isPlayer1 ? 'player1Side' : 'player2Side';
    const player = state[playerKey];

    // 尋找墓地中符合條件的卡
    const graveyard = [...player.graveyard];
    const cardsToSummon: Card[] = [];

    for (let i = graveyard.length - 1; i >= 0 && cardsToSummon.length < count; i--) {
      if (!cardName || graveyard[i].definitionId.includes(cardName)) {
        cardsToSummon.push(graveyard.splice(i, 1)[0]);
      }
    }

    // 尋找空位並放置
    let newState = { ...state, [playerKey]: { ...player, graveyard } };
    const side = newState.battlefield[sideKey];
    const emptySlots: { row: 'front' | 'back'; column: number }[] = [];

    side.frontLine.forEach((slot, idx) => {
      if (!slot.unit) emptySlots.push({ row: 'front', column: idx + 1 });
    });
    side.backLine.forEach((slot, idx) => {
      if (!slot.unit) emptySlots.push({ row: 'back', column: idx + 1 });
    });

    const toPlace = Math.min(cardsToSummon.length, emptySlots.length);

    for (let i = 0; i < toPlace; i++) {
      const card = cardsToSummon[i];
      const slot = emptySlots[i];

      const lineKey = slot.row === 'front' ? 'frontLine' : 'backLine';
      const newBattlefield = { ...newState.battlefield };
      const newSide = { ...newBattlefield[sideKey] };
      const newLine = [...newSide[lineKey]];

      newLine[slot.column - 1] = {
        ...newLine[slot.column - 1],
        unit: {
          ...card,
          location: 'battlefield',
          position: {
            side: isPlayer1 ? 'player1' : 'player2',
            row: slot.row,
            column: slot.column as 1 | 2 | 3 | 4 | 5,
          },
          orientation: 'upright',
          summonedThisTurn: true,
        },
      };

      newSide[lineKey] = newLine;
      newBattlefield[sideKey] = newSide;
      newState = { ...newState, battlefield: newBattlefield };
    }

    log.debug(`Summoned ${String(toPlace)} units from graveyard`);
    return newState;
  }
}

/**
 * 回手牌效果處理器（增強版）
 */
export class ReturnToHandHandlerV2 implements EffectHandler {
  canHandle(text: string): boolean {
    return text.includes('回手牌');
  }

  parse(effectText: string): ImmediateAction[] {
    const actions: ImmediateAction[] = [];

    // 解析「使敵方場上X單位回手牌」
    const returnMatch = effectText.match(/使(敵方|你)(場上)?(\d*)個?([^回]+)?回手牌/);
    if (returnMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'returnToHand',
        targetSelector: {
          zone: 'battlefield',
          side: returnMatch[1] === '敵方' ? 'enemy' : 'self',
          count: returnMatch[3] ? parseInt(returnMatch[3], 10) : 1,
        },
      });
    }

    // 解析「使你場上或墓地X卡回手牌」
    const multiZoneMatch = effectText.match(/使你場上或墓地(\d*)張?([^卡]+)卡回手牌/);
    if (multiZoneMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'returnFromMultiZone',
        value: multiZoneMatch[2].trim(),
        targetSelector: {
          zone: 'battlefield',
          side: 'self',
          count: multiZoneMatch[1] ? parseInt(multiZoneMatch[1], 10) : 1,
        },
      });
    }

    // 解析「使你墓地X魔法卡回手牌」
    const graveyardMatch = effectText.match(/使(你|敵方)墓地(\d*)張?([^回]+)?回.?手牌/);
    if (graveyardMatch) {
      actions.push({
        type: 'custom',
        sourceCardId: '',
        customHandler: 'returnFromGraveyard',
        value: graveyardMatch[3] ? graveyardMatch[3].trim() : undefined,
        targetSelector: {
          zone: 'graveyard',
          side: graveyardMatch[1] === '敵方' ? 'enemy' : 'self',
          count: graveyardMatch[2] ? parseInt(graveyardMatch[2], 10) : 1,
        },
      });
    }

    return actions;
  }

  execute(state: GameState, action: ImmediateAction): GameState {
    const selector = action.targetSelector;
    if (!selector) return state;

    const isPlayer1 = state.activePlayerId === state.player1.id;

    if (action.customHandler === 'returnToHand') {
      const count = selector.count ?? 1;
      const isEnemy = selector.side === 'enemy';
      const targetIsPlayer1 = isEnemy ? !isPlayer1 : isPlayer1;
      const sideKey = targetIsPlayer1 ? 'player1Side' : 'player2Side';
      const playerKey = targetIsPlayer1 ? 'player1' : 'player2';

      let newState = state;
      let returned = 0;

      const returnFromLine = (lineKey: 'frontLine' | 'backLine'): void => {
        if (returned >= count) return;

        const side = newState.battlefield[sideKey];
        const line = [...side[lineKey]];
        const player = newState[playerKey];
        const hand = [...player.hand];

        for (let i = 0; i < line.length && returned < count; i++) {
          const returnedUnit = line[i].unit;
          if (returnedUnit) {
            hand.push({ ...returnedUnit, location: 'hand' as const, position: undefined });
            line[i] = { ...line[i], unit: null };
            returned++;
          }
        }

        newState = {
          ...newState,
          battlefield: {
            ...newState.battlefield,
            [sideKey]: {
              ...newState.battlefield[sideKey],
              [lineKey]: line,
            },
          },
          [playerKey]: {
            ...player,
            hand,
          },
        };
      };

      returnFromLine('frontLine');
      returnFromLine('backLine');

      log.debug(`Returned ${String(returned)} units to hand`);
      return newState;
    }

    if (action.customHandler === 'returnFromGraveyard') {
      const count = selector.count ?? 1;
      const isEnemy = selector.side === 'enemy';
      const targetIsPlayer1 = isEnemy ? !isPlayer1 : isPlayer1;
      const playerKey = targetIsPlayer1 ? 'player1' : 'player2';
      const player = state[playerKey];

      const graveyard = [...player.graveyard];
      const hand = [...player.hand];
      let returned = 0;

      for (let i = graveyard.length - 1; i >= 0 && returned < count; i--) {
        hand.push({ ...graveyard.splice(i, 1)[0], location: 'hand' as const });
        returned++;
      }

      log.debug(`Returned ${String(returned)} cards from graveyard to hand`);

      return {
        ...state,
        [playerKey]: {
          ...player,
          graveyard,
          hand,
        },
      };
    }

    return state;
  }
}

// 匯出所有戰場處理器
export const battlefieldHandlers = {
  MoveHandler,
  KidnapHandler,
  SummonToPositionHandler,
  OrientationHandler,
  ExileHandler,
  GraveyardSummonHandler,
  ReturnToHandHandlerV2,
};
